import { useEffect, useState, useMemo, useContext } from 'react';
import { Delete, Edit, Add, CheckCircle } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Loader, Page, PageTitleWithBack, Table } from '../../../components';
import { ExternalLink } from '../../../components/link/ExternalLink';
import { Button, Divider, Box } from '@mui/material';
import { ICustomPage } from '../../../models';
import { useHistory } from 'react-router-dom';
import { AddCustomPageModal } from './AddCustomPageModal';
import { deleteCustomPage, getCustomPages } from '../../../fetch';
import {
  HELP_SLUG,
  HOMEOWNERS_PORTAL_SLUG,
  HOMEOWNERS_PORTAL_THRIVE_SLUG,
  SALES_APP_SLUG,
  OSC_REPORT_SLUG
} from '../../../helpers';
import { useFlags } from '../../../hooks';
import { UserContext } from '../../../context';

export const CustomPages = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [customPages, setCustomPages] = useState<ICustomPage[]>([]);
  const [isLoadingCustomPages, setLoadingCustomPages] = useState<boolean>(true);
  const [isDeleting, setDeletingCustomPage] = useState<boolean>(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const { authorizeUserNavigationAccess } = useFlags();
  const { isAdmin } = useContext(UserContext);
  const history = useHistory();

  const fetchCustomPages = async () => {
    try {
      setLoadingCustomPages(true);
      const res = await getCustomPages();
      setCustomPages(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading custom pages, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingCustomPages(false);
    }
  };

  const handleEdit = (page: ICustomPage) => {
    history.push(`/admin/manage-content/custom-pages/${page.slug}`);
  };
  const handleApplyRole = () => {
    history.push(`/admin/user-tools/unified-user-management/manage-roles-and-titles`);
  };

  const handleDelete = async (page: ICustomPage) => {
    const result = window.confirm('Are you sure you want to remove this custom page?');
    if (result) {
      setDeletingCustomPage(true);
      try {
        await deleteCustomPage(page.customPageId);

        enqueueSnackbar(`Custom page deleted!`, {
          variant: 'success'
        });
        fetchCustomPages();
      } catch (error) {
        console.log(error);
        enqueueSnackbar(`This custom page was unable to be deleted, please try again.`, {
          variant: 'error'
        });
      } finally {
        setDeletingCustomPage(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Slug',
        accessor: 'slug',
        sort: false,
        Cell: props => (
          <ExternalLink
            target="_blank"
            to={`${window.location.origin}/custom-pages/${props.cell.row.original.slug}`}
          >
            {props.cell.row.original.slug}
          </ExternalLink>
        )
      },
      {
        Header: 'Active',
        accessor: 'isActive',
        sort: false,
        Cell: props => <>{props.cell.row.original.isActive ? 'Yes' : 'No'}</>
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICustomPage } };
        }) => {
          return (
            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  handleEdit(original);
                }}
              >
                Edit
              </Button>

              <Button
                disabled={
                  original.slug === HOMEOWNERS_PORTAL_SLUG ||
                  original.slug === HOMEOWNERS_PORTAL_THRIVE_SLUG ||
                  original.slug === HELP_SLUG ||
                  original.slug === SALES_APP_SLUG ||
                  original.slug === OSC_REPORT_SLUG
                }
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original);
                }}
              >
                Delete
              </Button>
              {!original.hasNavigationRules && isAdmin && authorizeUserNavigationAccess && (
                <Button
                  color="secondary"
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    handleApplyRole();
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Apply Role
                </Button>
              )}
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPages]);

  useEffect(() => {
    fetchCustomPages();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Custom Pages">
        <PageTitleWithBack
          showDivider={false}
          backText="Back to Admin home"
          title="Custom Pages"
          backUrl="/admin"
          additionalHeaderContent={
            <Box display="flex" gap={1}>
              <Button
                startIcon={<Add />}
                id="custom-page-add"
                onClick={() => setAddModalOpen(true)}
                color="primary"
                variant="contained"
              >
                Add Custom Page
              </Button>
            </Box>
          }
        />
        <Divider />
        <Table
          columns={columns}
          data={customPages}
          isLoading={isLoadingCustomPages}
          hidePagination
        />
      </Page>
      <AddCustomPageModal
        open={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        customPages={customPages}
        fetchCustomPages={fetchCustomPages}
      />
    </>
  );
};
